import arrayLength from "@-/com.npmjs.extra-array__length";
import runes from "@-/com.npmjs.runes2";

import { isScorableToken } from "#sentence-drill-token";
import { isTokenSettled, type SentenceDrillState } from "#sentence-drill-state";

export type SentenceDrillTokenDisplay = {
  /** The glyph text to show, or `null` to render blanks. */
  glyphText: string | null;
  /** Bold marks a word the user was given — the "study this one" signal. */
  isBold: boolean;
  /** How many blanks to render when `glyphText` is null or partial. */
  pendingCharacterCount: number;
  /** Tone-number pinyin to show, or `null` to show none. Callers apply `toDiacriticPinyin`. */
  pinyinText: string | null;
};

/**
 * What to render for one token, in the current mode.
 *
 * The mode-dependence lives here rather than in the reducer so the state stays a pure record of
 * what happened, and the two modes cannot disagree about what a given status means.
 *
 * A `hinted` token in `speech` mode uncovers GLYPHS (never the reading — that would hand over the
 * pronunciation being tested); in `glyphs` mode it uncovers the PINYIN (never the characters —
 * those are the answer). `revealed` shows everything in both modes, bolded.
 */
export function sentenceDrillTokenDisplay(
  state: SentenceDrillState,
  index: number,
): SentenceDrillTokenDisplay {
  const token = state.tokens[index];
  const status = state.statusList[index];
  const empty: SentenceDrillTokenDisplay = {
    glyphText: null,
    isBold: false,
    pendingCharacterCount: 0,
    pinyinText: null,
  };
  if (token === undefined || status === undefined) {
    return empty;
  }

  const characterCount = arrayLength(runes(token.word));

  switch (status.kind) {
    case "filled": {
      // Getting a word right shows BOTH its glyph and its reading — the confirmation is the reward,
      // and seeing the pinyin it maps to is where the reinforcement happens.
      return {
        glyphText: token.word,
        isBold: false,
        pendingCharacterCount: 0,
        pinyinText: token.pinyin,
      };
    }
    case "revealed": {
      return {
        glyphText: token.word,
        isBold: true,
        pendingCharacterCount: 0,
        pinyinText: token.pinyin,
      };
    }
    case "hinted": {
      if (state.mode === "speech") {
        const shown = runes(token.word).slice(0, status.revealedCharacterCount).join("");
        return {
          glyphText: shown,
          isBold: false,
          pendingCharacterCount: characterCount - status.revealedCharacterCount,
          pinyinText: null,
        };
      }
      return {
        glyphText: null,
        isBold: false,
        pendingCharacterCount: characterCount,
        pinyinText: token.pinyin,
      };
    }
    case "pending": {
      return {
        glyphText: null,
        isBold: false,
        // Blanks are sized to the word so the sentence's shape is stable and the user can see how
        // many characters they still owe — a real signal, and it stops the layout jumping on fill.
        pendingCharacterCount: characterCount,
        pinyinText: null,
      };
    }
  }
}

export type SentenceDrillWordRating = {
  rating: "again" | "easy";
  tokenIndex: number;
};

/**
 * The ratings to apply when the drill ends.
 *
 * The scoring rule: every word the user PRODUCED is `easy`; a word that had to be revealed is
 * `again`. Hints alone do not demote — a word recalled after a nudge was still recalled.
 *
 * Everything still outstanding when the drill ended is OMITTED rather than rated. The user pressed
 * reveal on one blank; the words after it were never attempted, and scoring them would punish the
 * user for words they never got the chance to answer.
 *
 * Non-scorable tokens (punctuation) never appear: they have no card.
 */
export function sentenceDrillRatings(state: SentenceDrillState): SentenceDrillWordRating[] {
  const ratings: SentenceDrillWordRating[] = [];
  for (let index = 0; index < arrayLength(state.tokens); index++) {
    const token = state.tokens[index];
    const status = state.statusList[index];
    if (token === undefined || status === undefined || !isScorableToken(token)) {
      continue;
    }
    if (status.kind === "filled") {
      ratings.push({ rating: "easy", tokenIndex: index });
      continue;
    }
    if (status.kind === "revealed") {
      ratings.push({ rating: "again", tokenIndex: index });
    }
  }
  return ratings;
}

/** Whether every scorable token has been produced — the drill was completed without giving up. */
export function isSentenceDrillComplete(state: SentenceDrillState): boolean {
  return state.cursorIndex >= arrayLength(state.tokens);
}

/**
 * The ratings to apply when the learner ENDS an unfinished attempt with a card-level action — a
 * swipe or a rating button pressed before the sentence was completed or revealed.
 *
 * Same rule as {@link sentenceDrillRatings} for everything already settled: produced words stay
 * `easy`, revealed words stay `again`, and words the learner never reached are omitted. The one
 * addition is the word UNDER THE CURSOR — the blank they were working on when they acted — which
 * takes `currentWordRating`, the grade the action carries (Easy for a left swipe, Again for a
 * right swipe, or the button pressed). A drill that is already finished, or whose cursor has run
 * off the end, has no current word and reduces to the settled ratings alone.
 *
 * Returning the explicit rating rather than a bare `"again"` keeps the four-way grade the buttons
 * offer; callers that only know left/right map those to Easy/Again before calling.
 */
export type SentenceDrillCommittedWordRating<Rating> = {
  rating: Rating;
  tokenIndex: number;
};

export function sentenceDrillRatingsOnCommit<Rating>({
  currentWordRating,
  ratingFor,
  state,
}: {
  currentWordRating: Rating;
  /** Maps the settled `again` / `easy` outcomes onto the caller's rating type. */
  ratingFor: (outcome: SentenceDrillWordRating["rating"]) => Rating;
  state: SentenceDrillState;
}): SentenceDrillCommittedWordRating<Rating>[] {
  const ratings: SentenceDrillCommittedWordRating<Rating>[] = sentenceDrillRatings(state).map(
    ({ rating, tokenIndex }) => ({ rating: ratingFor(rating), tokenIndex }),
  );
  if (state.isFinished) {
    return ratings;
  }
  const currentToken = state.tokens[state.cursorIndex];
  const currentStatus = state.statusList[state.cursorIndex];
  if (
    currentToken === undefined ||
    currentStatus === undefined ||
    !isScorableToken(currentToken) ||
    isTokenSettled(currentStatus)
  ) {
    return ratings;
  }
  ratings.push({ rating: currentWordRating, tokenIndex: state.cursorIndex });
  ratings.sort((a, b) => a.tokenIndex - b.tokenIndex);
  return ratings;
}
